/**
 * Input Area — voice + text controls
 * Uses <img> tags with data URIs for reliable icon rendering in Shadow DOM
 */
import { el } from '../utils.js';

// ── Icon data URIs (encoded from Webflow SVGs) ──────────
// These use <img> tags instead of inline SVGs to avoid CSS sizing conflicts in Shadow DOM

// Mic icon (white stroke, for grey circle button)
const MIC_WHITE_URI = `data:image/svg+xml,${encodeURIComponent('<svg width="24" height="24" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 18V21M8 18C9.85652 18 11.637 17.2625 12.9497 15.9497C14.2625 14.637 15 12.8565 15 11V9M8 18C6.14348 18 4.36301 17.2625 3.05025 15.9497C1.7375 14.637 1 12.8565 1 11V9M8 1C9.65685 1 11 2.34315 11 4V11C11 12.6569 9.65685 14 8 14C6.34315 14 5 12.6569 5 11V4C5 2.34315 6.34315 1 8 1Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>')}`;

// Mic-off icon (white stroke, for listening state)
const MIC_OFF_WHITE_URI = `data:image/svg+xml,${encodeURIComponent('<svg width="24" height="24" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 18V21M14 8.34V4C13.9959 3.32637 13.7653 2.67371 13.3452 2.14712C12.925 1.62053 12.3399 1.25067 11.684 1.09708C11.0281 0.943502 10.3396 1.01514 9.72938 1.30047C9.11915 1.58579 8.62271 2.0682 8.32 2.67M15.95 15.95C14.971 16.9291 13.7237 17.5958 12.3658 17.866C11.0078 18.1361 9.60029 17.9975 8.32115 17.4676C7.042 16.9378 5.94872 16.0405 5.17955 14.8893C4.41039 13.738 3.9999 12.3845 4 11V9M17.89 12.23C17.9628 11.824 17.9996 11.4124 18 11V9M1 1L21 21M8 8V11C8.00052 11.593 8.17675 12.1725 8.50643 12.6653C8.83611 13.1582 9.30445 13.5423 9.85231 13.7691C10.4002 13.996 11.0029 14.0554 11.5845 13.9399C12.1661 13.8243 12.7005 13.539 13.12 13.12" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>')}`;

// Mic outline (dark, for mode indicator & switch)
const MIC_DARK_URI = `data:image/svg+xml,${encodeURIComponent('<svg width="20" height="20" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 18V21M8 18C9.85652 18 11.637 17.2625 12.9497 15.9497C14.2625 14.637 15 12.8565 15 11V9M8 18C6.14348 18 4.36301 17.2625 3.05025 15.9497C1.7375 14.637 1 12.8565 1 11V9M8 1C9.65685 1 11 2.34315 11 4V11C11 12.6569 9.65685 14 8 14C6.34315 14 5 12.6569 5 11V4C5 2.34315 6.34315 1 8 1Z" stroke="#334155" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>')}`;

// Keyboard (dark, for mode indicator & switch)
const KB_DARK_URI = `data:image/svg+xml,${encodeURIComponent('<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 9a1 1 0 11-2 0 1 1 0 012 0zm0 3a1 1 0 11-2 0 1 1 0 012 0zm3-3a1 1 0 11-2 0 1 1 0 012 0zm0 3a1 1 0 11-2 0 1 1 0 012 0zm3-3a1 1 0 11-2 0 1 1 0 012 0zm0 3a1 1 0 11-2 0 1 1 0 012 0zm3-3a1 1 0 11-2 0 1 1 0 012 0zm0 3a1 1 0 11-2 0 1 1 0 012 0zm3-3a1 1 0 11-2 0 1 1 0 012 0zm0 3a1 1 0 11-2 0 1 1 0 012 0z" fill="#334155"/><path d="M2 11C2 8.172 2 6.757 2.879 5.879 3.757 5 5.172 5 8 5h8c2.828 0 4.243 0 5.121.879C22 6.757 22 8.172 22 11v2c0 2.828 0 4.243-.879 5.121C20.243 19 18.828 19 16 19H8c-2.828 0-4.243 0-5.121-.879C2 17.243 2 15.828 2 13v-2z" stroke="#334155"/><path d="M7 16h10" stroke="#334155" stroke-linecap="round"/></svg>')}`;

const SEND_ICON_URL = 'https://cdn.prod.website-files.com/6926bba516d26bcae1fa654e/6926bba516d26bcae1fa656c_aiint__icon%20send.svg';

/**
 * Create the input area (voice + text controls)
 * @param {import('../widget.js').Widget} widget
 */
export function createInputArea(widget) {
    const inputAreaEl = el('div', { className: 'gr-livetrans__talkchat' });
    const formBlock = el('div', { className: 'gr-livetrans__talkchat-formblock w-form' });
    const formInner = el('div', { className: 'gr-livetrans__talk-row-chat' });

    // ── Top row: mode indicators + mic button ────────
    const talkRowTop = el('div', { className: 'gr-livetrans__talk-row-top' });

    // Voice mode indicator
    const voiceMode = el('div', { className: 'gr-livetrans__voice-mode' }, [
        el('div', { className: 'gr-livetrans__mode-icon' }, [
            el('img', { src: MIC_DARK_URI, alt: '', style: 'width:80%;height:80%' }),
        ]),
        el('div', {}, 'Voice Mode Active'),
    ]);

    // Text mode indicator
    const textMode = el('div', { className: 'gr-livetrans__text-mode' }, [
        el('div', { className: 'gr-livetrans__mode-icon type' }, [
            el('img', { src: KB_DARK_URI, alt: '', style: 'width:80%;height:80%' }),
        ]),
        el('div', {}, 'Text Mode Active'),
    ]);

    // Mic button
    const micNewBtn = el('div', { className: 'gr-livetrans__talk-new' }, [
        el('div', { className: 'gr-livetrans__talk-mic-on' }, [
            el('img', { src: MIC_OFF_WHITE_URI, alt: '', width: 24, height: 24 }),
        ]),
    ]);

    const micListeningBtn = el('div', { className: 'gr-livetrans__listening' }, [
        el('div', { className: 'gr-livetrans__talk-mic-off-icon' }, [
            el('img', { src: MIC_DARK_URI, alt: '', width: 24, height: 24 }),
        ]),
    ]);

    const talkDiv = el('div', { className: 'gr-livetrans__talk-div' }, [
        micNewBtn, micListeningBtn,
    ]);

    talkRowTop.appendChild(voiceMode);
    talkRowTop.appendChild(textMode);
    talkRowTop.appendChild(talkDiv);
    formInner.appendChild(talkRowTop);

    // ── Text input row ───────────────────────────────
    const inputField = el('input', {
        className: 'gr-chat-input-field w-input',
        type: 'text',
        maxLength: 256,
        placeholder: 'Type your reply',
    });
    inputField.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && inputField.value.trim()) {
            widget.sendMessage(inputField.value);
            inputField.value = '';
        }
    });
    // Notify server that consumer is typing (debounced in widget)
    inputField.addEventListener('input', () => {
        widget.notifyTyping?.();
    });

    const enterIndicator = el('div', { className: 'gr-enter-indicator' }, [
        el('div', {}, '↵ Enter'),
    ]);

    const sendBtn = el('div', {
        className: 'gr-livetrans__type-btn-send', onClick: () => {
            if (inputField.value.trim()) {
                widget.sendMessage(inputField.value);
                inputField.value = '';
            }
        }
    }, [el('img', { src: SEND_ICON_URL, loading: 'lazy', alt: '' })]);
    const sendBtnDiv = el('div', { className: 'gr-livetrans__type-btn-div' }, [sendBtn]);

    const typeNew = el('div', { className: 'gr-livetrans__type-new' }, [
        inputField, enterIndicator, sendBtnDiv,
    ]);
    const typeRowInput = el('div', { className: 'gr-livetrans__type-row-input' }, [typeNew]);
    formInner.appendChild(typeRowInput);

    // ── Status indicators ────────────────────────────
    const statusDiv = el('div', { className: 'gr-livetrans__status' });
    const statusProcessing = el('div', { className: 'gr-livetrans__status-processing' }, [
        el('div', { className: 'gr-livetrans__status-processing-animation' }),
        el('div', {}, 'Processing speech...'),
    ]);
    statusDiv.appendChild(statusProcessing);
    const arrowSvgUri = `data:image/svg+xml,${encodeURIComponent('<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20,80 Q70,70 85,20" stroke="#dc2626" stroke-width="8" stroke-linecap="round" fill="none"/><path d="M60,25 L85,20 L88,48" stroke="#dc2626" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>')}`;
    const arrowIndicator = el('img', {
        src: arrowSvgUri,
        className: 'gr-livetrans__mic-arrow',
        alt: '',
    });

    const statusIdle = el('div', { className: 'gr-livetrans__status-idle' }, [
        el('div', {}, [el('strong', {}, 'Ready to listen')]),
        el('div', {}, 'Tap the mic button to speak'),
        arrowIndicator,
    ]);
    statusDiv.appendChild(statusIdle);
    // Waveform bars for listening animation
    const waveformDiv = el('div', { className: 'gr-waveform' });
    for (let i = 0; i < 5; i++) {
        const bar = el('div', { className: 'gr-waveform__bar' });
        bar.style.animationDelay = `${i * 0.15}s`;
        waveformDiv.appendChild(bar);
    }
    const statusListening = el('div', { className: 'gr-livetrans__status-listening' }, [
        waveformDiv,
        el('div', {}, 'Listening...'),
    ]);
    statusDiv.appendChild(statusListening);
    formInner.appendChild(statusDiv);

    // ── Mode switch buttons ──────────────────────────
    const typeSwitch = el('div', { className: 'gr-livetrans__type-switch', style: 'cursor:pointer' }, [
        el('div', {}, [el('img', { src: KB_DARK_URI, alt: '', width: 20, height: 20 })]),
        el('div', {}, 'Switch to typing mode'),
    ]);

    const btmArrowSvgUri = `data:image/svg+xml,${encodeURIComponent('<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20,50 L85,50" stroke="#dc2626" stroke-width="10" stroke-linecap="round"/><path d="M55,25 L85,50 L55,75" stroke="#dc2626" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/></svg>')}`;

    const talkSwitch = el('div', { className: 'gr-livetrans__talk-switch', style: 'cursor:pointer' }, [
        el('div', { style: 'display:flex; align-items:center;' }, [
            el('img', { src: btmArrowSvgUri, className: 'gr-livetrans__btm-arrow', alt: '' }),
            el('img', { src: MIC_DARK_URI, alt: '', width: 20, height: 15 })
        ]),
        el('div', {}, [
            document.createTextNode('click to switch to '),
            el('strong', {}, 'voice mode')
        ]),
    ]);

    formInner.appendChild(typeSwitch);
    formInner.appendChild(talkSwitch);

    formBlock.appendChild(formInner);
    inputAreaEl.appendChild(formBlock);

    return {
        inputAreaEl, inputField, voiceMode, textMode,
        talkSwitch, typeSwitch, talkDiv, statusDiv,
        statusProcessing, statusIdle, statusListening,
        micNewBtn, micListeningBtn, typeRowInput,
    };
}
